import { createElement } from 'react'
import { toast } from 'sonner'
import { Lock } from 'lucide-react'
import { log } from '../logger'

export type ApiError = {
  /** Legacy normalized HTTP status used by existing client callers. */
  statusCode: number
  /** RFC 7807 HTTP status returned by the API. */
  status?: number
  code: string
  error?: string
  title?: string
  message: string
  detail?: string
  errors?: { field: string; message: string }[]
}

let _getToken: (() => Promise<string>) | null = null
let _redirectToLogin: (() => Promise<void> | void) | null = null
let _redirectInFlight = false

export function setTokenGetter(fn: () => Promise<string>) {
  _getToken = fn
}

/**
 * Register a callback that sends the user to the Auth0 login page. Used when
 * silent token refresh fails or when the API returns 401 — both cases mean
 * the session is unrecoverable and we must hand the user back to Auth0.
 *
 * Wired in app.tsx alongside setTokenGetter. Idempotent — repeated 401s in
 * the same session won't spawn multiple redirects (`_redirectInFlight` guard).
 */
export function setLoginRedirect(fn: () => Promise<void> | void) {
  _redirectToLogin = fn
}

/**
 * Centralized session-expired handler. Toast + redirect happen exactly ONCE
 * per session — concurrent failed requests / TanStack Query retries call
 * this freely; the `_redirectInFlight` guard ensures only the first call
 * actually surfaces UI. Returns true if this call started the redirect,
 * false if a redirect was already in flight or no redirect is registered.
 */
async function triggerLoginRedirect(): Promise<boolean> {
  if (_redirectInFlight || !_redirectToLogin) return false
  _redirectInFlight = true
  toast.error('Session expired — redirecting to login...')
  log.warn('Session expired, redirecting to Auth0 login')
  try {
    await _redirectToLogin()
  } catch {
    // Last resort: hard reload to / so Auth0Provider re-runs initial auth flow.
    window.location.href = '/'
  }
  return true
}

/** Test-only — reset module state between vitest cases. */
export function __resetAuthRecovery() {
  _getToken = null
  _redirectToLogin = null
  _redirectInFlight = false
}

/**
 * Get the current auth token. Used by SSE client which cannot use apiClient
 * (apiClient parses JSON; SSE needs raw ReadableStream).
 */
export async function getAuthToken(): Promise<string> {
  if (!_getToken) {
    throw new Error('Token getter not initialized. Call setTokenGetter() first.')
  }
  return _getToken()
}

export interface ApiClientOptions extends RequestInit {
  /**
   * RFC 7807 error codes for which to suppress the global toast. The promise
   * still rejects with the parsed ApiError; callers decide how to surface.
   * Phase 29 use case: EXEC_CONVERSATION_NO_SIGNAL is rendered as an inline
   * <Alert> inside ExecuteFromConversationDialog, so the global toast is noise.
   */
  suppressToastForCodes?: string[]
  /**
   * When true, a 401 response does NOT trigger the global session-expired
   * redirect to Auth0. Instead the 401 falls through to standard error
   * handling (toast + reject). Use for endpoints that proxy a third-party
   * service (Google Drive, ClickUp): a 401 from those calls reflects an
   * upstream OAuth/permission failure, not an Auth0 session problem.
   */
  skipAuthRedirectOn401?: boolean
}

function normalizeApiError(body: unknown, response: Response): ApiError {
  const parsed = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
  const statusCode =
    typeof parsed.statusCode === 'number'
      ? parsed.statusCode
      : typeof parsed.status === 'number'
        ? parsed.status
        : response.status

  return {
    ...parsed,
    statusCode,
    code: typeof parsed.code === 'string' ? parsed.code : `HTTP_${statusCode}`,
    error:
      typeof parsed.error === 'string'
        ? parsed.error
        : typeof parsed.title === 'string'
          ? parsed.title
          : response.statusText,
    message:
      typeof parsed.message === 'string'
        ? parsed.message
        : typeof parsed.detail === 'string'
          ? parsed.detail
          : response.statusText || `Request failed with status ${response.status}`,
  } as ApiError
}

export async function apiClient<T>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_URL
  const { suppressToastForCodes, skipAuthRedirectOn401, ...fetchOptions } = options

  const headers = new Headers(fetchOptions.headers)
  headers.set('Content-Type', 'application/json')

  if (_getToken) {
    let token: string
    try {
      token = await _getToken()
    } catch (err) {
      // Silent refresh failed (refresh token expired/revoked, network blip, etc.).
      // Hand the user back to Auth0 — toast + redirect are centralized in
      // triggerLoginRedirect() and guarded by `_redirectInFlight`, so concurrent
      // failed requests under TanStack Query's retry policy can call this freely
      // without spamming duplicate toasts.
      void triggerLoginRedirect()
      throw err
    }
    headers.set('Authorization', `Bearer ${token}`)
  }

  let response: Response
  try {
    // credentials:'include' is required for the kp_preview_session cookie
    // set by POST /api/v1/knowledge-packs/.../preview-link to round-trip on
    // subsequent requests (Phase 32-09 — Gap G5). CORS already permits
    // credentials on the API side (api/src/plugins/security.ts line 23:
    // `credentials: true`), so this client-side flag is the missing piece
    // for the browser to actually retain the Set-Cookie response header.
    response = await fetch(`${baseUrl}${path}`, {
      ...fetchOptions,
      headers,
      credentials: 'include',
    })
  } catch (err) {
    toast.error('Unable to connect. Please check your connection and try again.')
    log.error('API request failed to connect', {
      path,
      reason: err instanceof Error ? err.message : String(err),
    })
    throw err
  }

  // Server-side rejected the access token (revoked / wrong audience / expired).
  // Same outcome as silent-refresh failure: send the user back to Auth0.
  // triggerLoginRedirect() owns the toast and the `_redirectInFlight` guard,
  // so multiple concurrent 401s won't double-toast.
  //
  // Exception: callers passing `skipAuthRedirectOn401` opt out — used by
  // connector browse/export endpoints where a 401 is an upstream Drive /
  // ClickUp OAuth failure, not an Auth0 session expiry. Those fall through
  // to the standard error-toast path below.
  if (response.status === 401 && !skipAuthRedirectOn401) {
    void triggerLoginRedirect()
    let error: ApiError
    try {
      error = normalizeApiError(await response.json(), response)
    } catch {
      error = { statusCode: 401, code: 'UNAUTHORIZED', error: 'Unauthorized', message: 'Session expired' }
    }
    throw error
  }

  if (!response.ok) {
    let error: ApiError
    try {
      error = normalizeApiError(await response.json(), response)
    } catch {
      error = {
        statusCode: response.status,
        code: 'UNKNOWN',
        error: response.statusText,
        message: `Request failed with status ${response.status}`,
      }
    }

    // Server-side failures are worth a structured log (4xx -> warn, 5xx ->
    // error) regardless of whether the toast is suppressed for this code.
    log[response.status >= 500 ? 'error' : 'warn']('API request failed', {
      path,
      status: response.status,
      code: error.code,
    })

    const shouldToast = !suppressToastForCodes?.includes(error.code)
    if (shouldToast) {
      if (response.status === 403) {
        toast.warning(error.message || "You don't have permission to perform this action", {
          icon: createElement(Lock, { className: 'size-4' }),
        })
      } else {
        toast.error(error.message || 'An error occurred')
      }
    }
    throw error
  }

  // 204 No Content never has a body.
  if (response.status === 204) {
    return undefined as T
  }

  // 202 Accepted may carry a JSON body (e.g. import endpoints return
  // `{ imported: [...] }`, update-from-source returns `{ status, fileId }`).
  // Parse when Content-Type is JSON; fall back to undefined when the
  // response is empty (some 202 callers don't echo a body).
  if (response.status === 202) {
    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('application/json')) {
      return undefined as T
    }
    try {
      return await response.json()
    } catch {
      return undefined as T
    }
  }

  return response.json()
}
